import { ApiError } from '@/lib/api';
import type {
  ModelNumericPath,
  ModelParamError,
  ModelParams,
  ModelRoute,
  ModelSeatKey,
  ModelSettingPath,
  ModelTariff,
  ModelTestCode,
  ModelTestStatus,
  ModelTextPath,
  ModelWarning,
  SeatPin,
} from '@matter_extractor/shared';
import {
  MODEL_SEAT_KEYS,
  MODEL_SETTINGS,
  MODEL_SHAPE_PATH,
  modelPricingPath,
  modelSeatPath,
  readModelSetting,
  seatPinOfParams,
} from '@matter_extractor/shared';
import { modelParamErrorSchema } from '@matter_extractor/shared';
import { z } from 'zod';

/**
 * The words the models screen speaks (§11.1, §11.2).
 *
 * What it does NOT own is the name of a setting: those live in
 * `MODEL_SETTINGS`, one list in `@matter_extractor/shared`, so a seat cannot be
 * called one thing on the screen and another in the journal that records it
 * moving.
 *
 * What it does own is every refusal and every warning, worded from the `code`
 * the server sends. The server's `message` is an English developer string - it
 * belongs to the logs and to the OpenAPI description, never to an operator's
 * screen.
 */

/**
 * The paths a journal entry written BEFORE the seats were renamed carries.
 *
 * The journal is append-only - nothing rewrites what it already holds - so a
 * deploy migrated by #61 keeps lines naming `interviewerModel.model` and
 * `judge2.route`. Without this map, `settingLabel` falls back on the raw path
 * and an operator reads a developer identifier on the one screen whose whole
 * job is to be readable six weeks later.
 *
 * A map that has to NAME its entries rather than a rule stripping a suffix: the
 * aliasing is a historical fact about a fixed set of paths, not a pattern a
 * future seat should silently join. The two judges have no successor at all, so
 * they get their own sentence rather than a target path - « un siège qui
 * n'existe plus » is the true answer, and pointing them at a living seat would
 * make the journal claim a move that never happened.
 */
const LEGACY_SETTING_PATH: Readonly<Record<string, ModelSettingPath>> = {
  // Before BUILD-830 a conducting seat was ONE setting; since, it is three.
  interviewerModel: 'interviewer.model',
  scribeModel: 'scribe.model',
  'interviewerModel.route': 'interviewer.route',
  'interviewerModel.model': 'interviewer.model',
  'interviewerModel.provider': 'interviewer.provider',
  'scribeModel.route': 'scribe.route',
  'scribeModel.model': 'scribe.model',
  'scribeModel.provider': 'scribe.provider',
};

/** The seats of before #61 that nothing replaced - named, never redirected. */
const RETIRED_SETTING_LABEL: Readonly<Record<string, string>> = {
  judge1: 'le premier évaluateur (siège retiré)',
  'judge1.route': 'la porte du premier évaluateur (siège retiré)',
  'judge1.model': 'le modèle du premier évaluateur (siège retiré)',
  'judge1.provider': 'l’hébergeur du premier évaluateur (siège retiré)',
  judge2: 'le second évaluateur (siège retiré)',
  'judge2.route': 'la porte du second évaluateur (siège retiré)',
  'judge2.model': 'le modèle du second évaluateur (siège retiré)',
  'judge2.provider': 'l’hébergeur du second évaluateur (siège retiré)',
};

function currentPath(path: string): string {
  return LEGACY_SETTING_PATH[path] ?? path;
}

export function settingLabel(path: string): string {
  if (path === MODEL_SHAPE_PATH) return 'la forme des sièges';
  const retired = RETIRED_SETTING_LABEL[path];
  if (retired !== undefined) return retired;
  const resolved = currentPath(path);
  return MODEL_SETTINGS.find((setting) => setting.path === resolved)?.label ?? resolved;
}

/** Which seat a path belongs to, or null for the budget and the retired ones. */
export function seatOf(path: string): ModelSeatKey | null {
  const resolved = currentPath(path);
  return MODEL_SETTINGS.find((setting) => setting.path === resolved)?.seat ?? null;
}

/** How a door is named to an operator - one table, both screens. */
export const ROUTE_LABELS: Record<ModelRoute, string> = {
  openrouter: 'le routeur (un hébergeur au choix, pas de cache de prompt)',
  anthropic: 'l’API directe (cache de prompt système)',
};

/**
 * A refusal, in the team's words. A `Record` on the closed code set: adding a
 * code to the schema without a sentence here stops compiling.
 */
export const REFUSAL_WORDING: Record<ModelParamError['code'], string> = {
  'sampling-not-supported':
    'ce modèle n’accepte plus de réglage d’échantillonnage, alors que ce siège en envoie un - chaque appel échouerait',
  'pricing-negative':
    'un tarif négatif ferait baisser le coût d’une étape à chaque appel : le plafond de §11.2 ne se déclencherait jamais',
  'budget-negative':
    'un budget négatif est franchi par le premier appel de chaque étape, ce qui fermerait l’étape avant qu’elle ait posé sa première question',
};

/**
 * A warning, in the team's words. It is INFORMATIVE: the write went through,
 * and refusing an unknown model would put the next model release behind un
 * redéploiement - the exact problem this screen exists to remove.
 */
export const WARNING_WORDING: Record<ModelWarning['code'], string> = {
  'unknown-sampling-support':
    'ce modèle n’est pas dans la table des réglages connus : il est accepté tel quel, et personne n’a vérifié ce qu’il fait du réglage d’échantillonnage',
  'route-model-mismatch':
    'ce nom de modèle n’est pas écrit comme cette porte nomme les siens - le routeur préfixe par un fournisseur (anthropic/claude-…), l’API directe non. S’il est vraiment écrit comme ça chez elle, ignorez cette phrase ; sinon chaque appel de ce siège échouera',
  'pricing-missing':
    'aucun tarif n’est posé pour ce siège : ses appels comptent pour zéro euro, donc le budget IA par étape ne peut jamais être atteint et le plafond ne protège rien',
};

/**
 * The FIVE seats of §11.1, in the order the panel walks them: the two who hold
 * the conversation, the one who writes for l’opérateur, then the two who read
 * the matter once the conversation is over - the rangeur that files it (#177),
 * the analyste that notes it (#149).
 *
 * It reads the shared list rather than re-spelling it: `MODEL_SEAT_KEYS` is the
 * order of a session in `shared`, and two orders would eventually put a card
 * and its journal line in different places.
 */
export const SEAT_KEY_ORDER: readonly ModelSeatKey[] = MODEL_SEAT_KEYS;

/** The heading of a seat's card. Named by what it DOES, never by its field. */
export const SEAT_KEY_LABELS: Record<ModelSeatKey, string> = {
  interviewer: 'L’interviewer (parle à la personne)',
  scribe: 'Le scribe (découpe et cote)',
  narrator: 'Le narrateur (propose une histoire)',
  ranger: 'Le rangeur (range la matière)',
  analyst: 'L’analyste (détermine le noyau)',
};

/** Which part of the dispositif a seat belongs to - an eyebrow above the title. */
export const SEAT_KEY_GROUP: Record<ModelSeatKey, string> = {
  interviewer: 'Conversation',
  scribe: 'Conversation',
  narrator: 'Rédaction',
  ranger: 'Analyse',
  analyst: 'Analyse',
};

/**
 * What an operator has to know about a seat before choosing anything.
 *
 * The interviewer's note names the two facts the spec asks to DISPLAY rather
 * than hide: a call is abandoned at sixty seconds, and the router does not
 * cache the system prompt. The narrator says the thing only it does - it is not
 * in the conversation at all - because somebody comparing four cards needs to
 * know which one an entretien never touches. The analyst carries the role line
 * of noyau.md §7, word for word.
 */
export const SEAT_KEY_NOTES: Record<ModelSeatKey, string> = {
  interviewer:
    'C’est lui qui formule chaque question. Sa qualité s’entend directement dans l’entretien. Un appel est abandonné au bout de 60 secondes : quelqu’un attend devant l’écran.',
  scribe:
    'Il lit chaque réponse, la découpe en fragments et propose une cotation. La personne ne le voit jamais. Il peut tourner plus léger que l’interviewer : il remplit une fiche, il ne parle à personne.',
  narrator:
    'Il écrit l’histoire qu’un persona propose (« Proposer une histoire », §10.1). Il n’entre jamais dans un entretien : il répond à un bouton de l’administration, et un déploiement qui ne peut pas l’atteindre mène quand même ses sessions.',
  ranger:
    'Range la matière d’une conversation sur les objectifs du protocole, une fois par session, avant l’analyste.',
  analyst: 'Détermine le noyau de la personne à la fin d’une expérience, une fois par session.',
};

/**
 * What a connection test found, in the team's words.
 *
 * A `Record` on the closed code set, same régime as `REFUSAL_WORDING`: adding a
 * code to the schema without a sentence here stops compiling. Each sentence
 * says what happened AND what it means for a real session - « aucun appel n’a
 * été tenté » is the half an operator most needs, because this is the only
 * button on the screen that talks to a provider.
 */
export const TEST_WORDING: Record<ModelTestCode, string> = {
  ok: 'le fournisseur a répondu. Rien à signaler.',
  'missing-key':
    'ce déploiement ne porte aucune clé pour cette porte : aucun appel n’a été tenté, et chaque appel de ce siège échouerait.',
  'unknown-model':
    'le fournisseur a refusé ce réglage. Le plus souvent le nom du modèle - faute de frappe, ou identifiant écrit comme l’autre porte nomme les siens ; parfois l’hébergeur épinglé, ou un modèle qui n’accepte plus le réglage d’échantillonnage que ce siège envoie.',
  'sampling-incoherent':
    'ce modèle n’accepte plus de réglage d’échantillonnage, alors que ce siège en envoie un - aucun appel n’a été tenté, et l’enregistrement serait refusé.',
  unreachable:
    'la porte a répondu autre chose, ou n’a pas répondu du tout : clé refusée, quota, panne du fournisseur.',
  timeout:
    'le fournisseur n’a pas répondu dans le court délai de ce test. Un vrai appel dispose de 60 secondes : ça ne dit pas que le réglage est faux, seulement que la porte est lente.',
  stub: 'aucun appel n’a été tenté : ce déploiement tourne en mode bouchonné, donc ce réglage n’est vérifié par personne ici.',
};

/**
 * Four severities, four words. The status is decided by the server.
 *
 * « Non testé » rather than « Réussi » on a stubbed deploy (BUILD-866) : the
 * audit typed a model id nobody serves and read « Réussi » on every seat, so
 * the word has to say what happened - rien - and not what an operator hoped
 * for.
 */
export const TEST_STATUS_WORDING: Record<ModelTestStatus, string> = {
  ok: 'Réussi',
  warning: 'À surveiller',
  error: 'Échec',
  skipped: 'Non testé',
};

/**
 * What the panel says when AT LEAST ONE seat came back stubbed.
 *
 * One is enough because the mode is a property of the deploy: the seats are
 * stubbed together, and the only thing that can preempt one of them is an
 * incoherent sampling pair, decided before the mode is read.
 */
export const TEST_STUBBED_NOTICE =
  'Aujourd’hui, ce déploiement ne parle à aucun fournisseur : il tourne en mode bouchonné et le conducteur scripté répond à la place des modèles. Le bouton dit donc ce qu’il a fait - rien - et un identifiant de modèle faux passerait ici sans être vu.';

/**
 * A euro figure.
 *
 * Under one cent is written as such rather than rounded to `0,00 €`: the server
 * rounds to the cent, and a seat on a cheap model genuinely lands below that.
 * A seat with NO tariff at all is a different sentence and not a small figure -
 * `pricing-missing` says it, and printing « 0,00 € » under it would read as a
 * measured cost rather than as an absent rate.
 */
export function formatCost(costEur: number): string {
  if (costEur === 0) return 'moins de 0,01 € / session';
  return `~${costEur.toFixed(2).replace('.', ',')} € / session`;
}

/** A euro amount as the field shows it - a plain decimal, never a currency. */
export function formatEuros(value: number): string {
  return String(value).replace('.', ',');
}

/**
 * A typed euro amount back into a number, or null when it is not one.
 *
 * `null` rather than `NaN` or a silent zero: an operator mid-typing has an
 * unparsable field for a keystroke or two, and a zero written into the
 * candidate at that moment would be a rate nobody chose - which the screen
 * would then warn about with `pricing-missing`. The field keeps its own text
 * and the candidate only moves on a value that parses.
 *
 * The comma is accepted because a French keyboard produces it, and a screen
 * that refuses the decimal separator of its own language is a screen that
 * teaches people to distrust it.
 */
export function parseEuros(text: string): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/** An endpoint nobody pinned is stored as `''`; printing that reads as a hole. */
export const UNPINNED_ENDPOINT = 'aucun hébergeur épinglé';

export function journalValue(path: string, value: string): string {
  if (value !== '') return value;
  return path.endsWith('.provider') ? UNPINNED_ENDPOINT : '(vide)';
}

/**
 * The typed refusals of a failed write, or none.
 *
 * The envelope carries `details` precisely so the screen can word the refusal
 * itself. Anything that does not parse is NOT a refusal this screen knows how
 * to say - it falls back to the generic failure rather than printing an English
 * string at an operator.
 */
export function modelRefusals(error: unknown): ModelParamError[] {
  if (!(error instanceof ApiError)) return [];
  const parsed = z.array(modelParamErrorSchema).safeParse(error.details);
  return parsed.success ? parsed.data : [];
}

/**
 * The refusals of « Enregistrer une nouvelle version » as ONE sentence, or
 * `null` when the failure carried none.
 *
 * The layer shows a refusal as a string under its save box, so the typed list
 * of `modelRefusals` is worded here, from the `code` of each, and never from the
 * server's `message`. Each refusal names the setting it is about; the model id
 * follows in parentheses only when there is one - the budget of §11.2 belongs
 * to no seat, and « ( ) » after a sentence reads as something the screen failed
 * to fill in.
 */
export function modelRefusalSentence(error: unknown): string | null {
  const refusals = modelRefusals(error);
  if (refusals.length === 0) return null;
  const lines = refusals.map(
    (refusal) =>
      `${settingLabel(refusal.path)} - ${REFUSAL_WORDING[refusal.code]}${
        refusal.model === '' ? '' : ` (${refusal.model})`
      }.`,
  );
  return `Cette configuration ne tient pas : ${lines.join(' ')} Rien n’a été écrit.`;
}

/**
 * The three settings of a seat's pin, and its two tariffs, spelled once.
 *
 * Delegating to `shared` rather than re-spelling the concatenation: the backend
 * builds the same paths to journal what moved, and two spellings would
 * eventually disagree about which setting a line names.
 */
export function seatPath(seat: ModelSeatKey, field: 'route' | 'model' | 'provider'): ModelTextPath {
  return modelSeatPath(seat, field);
}

export function pricingPath(
  seat: ModelSeatKey,
  field: 'inputEurPerMillion' | 'outputEurPerMillion',
): ModelNumericPath {
  return modelPricingPath(seat, field);
}

/** What a seat runs on, as the one pin format both screens display. */
export function seatPinOf(params: ModelParams, seat: ModelSeatKey): SeatPin {
  return seatPinOfParams(params, seat);
}

/**
 * The published rate this deploy knows for a model id, or null.
 *
 * Served by the server and never a second table here: `PRICE_TABLE` is one
 * list, and a copy in the browser would drift on the first rate that moves. A
 * SUGGESTION - what the seat carries is what counts the euros.
 */
export function tariffFor(tariffs: readonly ModelTariff[], model: string): ModelTariff | null {
  return tariffs.find((tariff) => tariff.model === model) ?? null;
}

/** Which settings the operator has moved, against what the base holds. */
export function movedSettings(stored: ModelParams, candidate: ModelParams): ModelSettingPath[] {
  return MODEL_SETTINGS.filter(
    (setting) =>
      readModelSetting(stored, setting.path) !== readModelSetting(candidate, setting.path),
  ).map((setting) => setting.path);
}
